import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  EditOutlined,
  PictureOutlined,
  UploadOutlined,
  UserOutlined,
  CameraOutlined,
} from "@ant-design/icons";
import {
  App,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Image,
  Input,
  Modal,
  Row,
  Select,
  Skeleton,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import Papa from "papaparse";
import Resizer from "react-image-file-resizer";
import * as XLSX from "xlsx";

import { Artwork } from "../../../data/models/artwork";
import { RootState } from "../../../redux/appState";
import { makeRequest } from "../../helpers/axios-manipulator";
import { getMarketplaceUrl } from "../../helpers/url";
import ImageCropper from "../../components/ImageCropper/ImageCropper";

const { Option } = Select;
const defaultImageUrl = "https://app.ujamaa.digital/images/defaultImage.svg";

const { Title, Text } = Typography;

const InventoryManagement = () => {
  const { message } = App.useApp();
  const user = useSelector((state: RootState) => state.user.user);

  const [showInventoryContent, setShowInventoryContent] = useState(false);
  const [data, setData] = useState<any>({});
  // Memoized artists and inventoryData for use in JSX

  const [selectedArtistId, setSelectedArtistId] = useState<string | null>(null);
  const [artistOptions, setArtistOptions] = useState<{ label: string; value: string }[]>([]);
  const [imageList, setImageList] = useState<File[]>([]);
  const [previewImages, setPreviewImages] = useState<{ name: string; url: string }[]>([]);
  const [templateData, setTemplateData] = useState<any[]>([]);
  const [isImageUploaded, setIsImageUploaded] = useState(false);
  const [isTemplateUploaded, setIsTemplateUploaded] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [newArtist, setNewArtist] = useState<string | null>(null);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isIndeterminate, setIsIndeterminate] = useState(false);
  const [rowStatuses, setRowStatuses] = useState<Record<string, 'pending' | 'success' | 'error'>>({});
  const [rowMessages, setRowMessages] = useState<Record<string, string>>({});
  const [showStatusColumn, setShowStatusColumn] = useState(false);
  const allUploadsDone = Object.keys(rowStatuses).length > 0 && Object.values(rowStatuses).every(status => status === "success");
  const successCount = Object.values(rowStatuses).filter(status => status === "success").length;
  const [selectionInput, setSelectionInput] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isImageEditorVisible, setIsImageEditorVisible] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState<Artwork | null>(null);
  const [editArtwork, setEditArtwork] = useState<Artwork | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [selectedInventoryRows, setSelectedInventoryRows] = useState<React.Key[]>([]);
  const [massCommission, setMassCommission] = useState<string>('');
  
  // New state for Rep Selection (Root Only)
  const [selectedTargetRepId, setSelectedTargetRepId] = useState<string | undefined>(undefined);
  // For Edit Modal (Root Only)
  const [modalTargetRepId, setModalTargetRepId] = useState<string | undefined>(undefined);

  // Extract reps from data for use in modal and elsewhere (declare only once, after all state/data)
  const reps = data?.reps || [];

    // Helper to check if user can edit artwork
    const canEditArtwork = (artwork: Artwork | null) => {
      if (!artwork || !user) return false;

      const isRoot = isRootUser() || user.profile_name === 'Root' || user.contract_id === 0 || user.contract_id === '0';
      const isOriginal = !artwork.copy_enabled;
      const isOriginalSold = isOriginal && artwork.current_owner !== artwork.created_by_artist_id;

      // Restrict if Original has been sold - No one can edit
      if (isOriginalSold) return false;
      
      // Root can always edit (unless original is sold)
      if (isRoot) return true;

      // Non-Root Logic
      
      // Restrict if updated_by_user_id equals created_by_artist_id (Artist touched it last)
      // We compare as strings to be safe
      if (String(artwork.updated_by_user_id) === String(artwork.created_by_artist_id)) {
        return false;
      }
      
      // If it's a Sold Print, we allow opening the modal (return true),
      // but specific fields will be disabled in the form.
      
      return true;
    };

    // Helper to grey out rows that cannot be edited
    const getRowClassName = (record: Artwork) => {
      if (!canEditArtwork(record)) {
        return 'greyed-row';
      }
      return '';
    };


  // For root users: handle rep selection
  const handleRepSelect = (value: string, recordKey: string) => {
    setTemplateData((prevData: any[]) =>
      prevData.map((row) => (row.key === recordKey ? { ...row, selectedRep: value } : row))
    );
  };

  // Handle edited image upload
  const handleImageEdited = async (croppedImageFile: File, width: number, height: number) => {
    console.log('handleImageEdited called with:', {
      fileName: croppedImageFile.name,
      fileSize: croppedImageFile.size,
      width,
      height
    });

    if (!editArtwork) {
      console.error('No editArtwork set');
      return;
    }

    setUploadingImage(true);
    const hideLoadingMessage = message.loading('Uploading image...', 0);

    try {
      // Step 1: Upload the cropped image to Google Cloud Storage
      const formData = new FormData();
      formData.append('file', croppedImageFile);

      const uploadResponse = await makeRequest({
        origin: window.location.origin,
        method: 'post',
        url: '/uploadImage',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (!uploadResponse || !uploadResponse.url) {
        throw new Error('Failed to upload image to storage');
      }

      hideLoadingMessage();
      const hideUpdatingMessage = message.loading('Updating artwork...', 0);

      // Step 2: Update the artwork with the new image URL (only send necessary fields)
      const updateResponse = await makeRequest({
        origin: window.location.origin,
        method: 'put',
        url: `/artwork/${editArtwork.id}`,
        data: {
          image_url: uploadResponse.url,
          updated_by_user_id: editArtwork.updated_by_user_id,
          created_by_artist_id: editArtwork.created_by_artist_id,
        },
        headers: {
          'Content-Type': 'application/json',
        },
      });

      hideUpdatingMessage();

      // Update the local state with the new image URL
      if (updateResponse) {
        setEditArtwork({ ...editArtwork, image_url: uploadResponse.url });
        setSelectedArtwork({ ...editArtwork, image_url: uploadResponse.url });
        message.success('Image updated successfully!');
      }

      setIsImageEditorVisible(false);

      // Refresh the inventory data to reflect the change
      await fetchKpiData();
    } catch (error: any) {
      hideLoadingMessage();
      console.error('Failed to upload edited image:', error);

      // Show user-friendly error message
      if (error?.message?.includes('EADDRNOTAVAIL') || error?.message?.includes('read')) {
        message.error('Database connection error. Please try again in a moment.');
      } else if (error?.response?.status === 403) {
        message.error('You do not have permission to edit this artwork.');
      } else if (error?.message?.includes('upload')) {
        message.error('Failed to upload image. Please check your connection and try again.');
      } else {
        message.error('Failed to update image. Please try again.');
      }
    } finally {
      setUploadingImage(false);
    }
  };

  // Check if all rows have a selected artist
  // Enable Save if at least one selected row is valid
  const checkIfSaveEnabled = () => {
    if (selectedRows.length === 0) {
      setIsSaveEnabled(false);
      return;
    }
    // All selected rows must have artist and image (and rep if root)
    const allSelectedValid = templateData
      .filter((row) => selectedRows.includes(String(row.key)))
      .every((row) =>
        row.selectedArtist && row.selectedArtist !== "" &&
        row.selectedImage && row.selectedImage !== "" &&
        (!data?.isRoot || (row.selectedRep && row.selectedRep !== ""))
      );
    setIsSaveEnabled(allSelectedValid);
  };

  // Check if all rows have a selected artist whenever data changes
  useEffect(() => {
    checkIfSaveEnabled();
  }, [templateData, selectedRows]);

  const handleUpdateSelectedArtists = () => {
    if (!newArtist) return; // Ensure an artist is selected before updating
  
    setTemplateData((prevData) =>
      prevData.map((row) =>
        selectedRows.includes(String(row.key)) // Convert key to string for comparison
          ? { ...row, selectedArtist: newArtist }
          : row
      )
    );
    setSelectedRows([]); // Clear selection after updating
  }

  const handleFolderSelect = async (info: any) => {
    const files: File[] = info.fileList.map((file: any) => file.originFileObj);
    
    // Filter only supported image formats (JPEG, PNG, HEIC, HEIF)
    const supportedFiles = files.filter(file => 
      ["image/jpeg", "image/png", "image/heic", "image/heif"].includes(file.type)
    );

    setImageList(supportedFiles);
    
    // Resize images for preview
    const chunkSize = 10;
    const resizedImages: { name: string; url: string }[] = [];

    for (let i = 0; i < supportedFiles.length; i += chunkSize) {
      const chunk = supportedFiles.slice(i, i + chunkSize);
      const chunkResults = await Promise.all(
        chunk.map(file =>
          new Promise<{ name: string/*; uid: string*/; url: string }>((resolve) => {
            let outputFormat = "JPEG"; // Default format
            
            if (file.type === "image/png") outputFormat = "PNG"; 
            // HEIC/HEIF might need external conversion before resizing

            Resizer.imageFileResizer(
              file,
              150, // Width
              150, // Height
              outputFormat, // Dynamic format based on file type
              50, // Quality
              0,
              (uri) => resolve({ name: file.name/*, uid: file.uid*/, url: uri as string }),
              "base64"
            );
          })
        )
      );
      resizedImages.push(...chunkResults);
      // Small delay to allow UI to breathe
      await new Promise(resolve => setTimeout(resolve, 0));
    }

    setPreviewImages(resizedImages);
    setIsImageUploaded(true);
  };

  const handleSave = async () => {
    setLoading(true);
    setShowStatusColumn(true);
    let failedUploads: any[] = [];
    
    // Only process selected rows
    const rowsToProcess = templateData.filter((row) => selectedRows.includes(String(row.key)));
    
    // Set all selected rows to pending
    setRowStatuses((prev) => {
      const newStatuses: Record<string, 'pending' | 'success' | 'error'> = {};
      templateData.forEach(row => {
        if (selectedRows.includes(String(row.key))) {
          newStatuses[row.key] = 'pending';
        }
      });
      return newStatuses;
    });
    setRowMessages({});

    const processedRowsMap = new Map();
    const BATCH_SIZE = 5;

    // Process image uploads in batches
    for (let i = 0; i < rowsToProcess.length; i += BATCH_SIZE) {
      const batch = rowsToProcess.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map(async (row) => {
        if (row.selectedImage) {
          let formData = new FormData();
          formData.append('rootUser', String(isRootUser()));
          formData.append('userId', user.id);
          const selectedImage = imageList.find(img => img.name === row.selectedImageFilename);
          if (selectedImage) {
            formData.append('file', selectedImage);
          } else {
            const msg = `Image with filename ${row.selectedImageFilename} not found in imageList.`;
            console.error(msg);
            failedUploads.push(row);
            setRowStatuses(prev => ({ ...prev, [row.key]: "error" }));
            setRowMessages(prev => ({ ...prev, [row.key]: msg }));
            processedRowsMap.set(row.key, row);
            return;
          }

          let attempt = 0;
          let success = false;
          let imageUrl = '';
          while (attempt < 3 && !success) {
            try {
              const response = await makeRequest({
                origin: window.location.origin,
                method: 'post',
                url: '/uploadImage',
                headers: {},
                data: formData,
                timeout: 60000,
              });
              
              if (!response.ok) throw new Error('Upload failed');
              imageUrl = response.url;
              success = true;
              setRowStatuses(prev => ({ ...prev, [row.key]: 'success' }));
            } catch (error: any) {
              console.error(`Upload failed for ${row.selectedImageFilename}, attempt ${attempt + 1}`, error);
              if (attempt === 2) {
                 setRowMessages(prev => ({ ...prev, [row.key]: error.message || "Upload failed" }));
              }
              attempt++;
            }
          }
          if (!success) {
            failedUploads.push(row);
            setRowStatuses(prev => ({ ...prev, [row.key]: "error" }));
            processedRowsMap.set(row.key, row);
            return;
          }
          processedRowsMap.set(row.key, { ...row, imageUrl: imageUrl });
        } else {
          processedRowsMap.set(row.key, { ...row, imageUrl: defaultImageUrl });
        }
      }));
    }

    // Update template data with processed results
    const updatedTemplateData = templateData.map(row => {
      if (processedRowsMap.has(row.key)) {
        return processedRowsMap.get(row.key);
      }
      return row;
    });

    setTemplateData(updatedTemplateData);

    if (failedUploads.length > 0) {
      alert(`Some images failed to upload. Please try again.`);
      setLoading(false);
      return;
    }

    // Onboard only selected artworks (also in batches)
    const rowsToOnboard = updatedTemplateData.filter((row) => selectedRows.includes(String(row.key)));
    for (let i = 0; i < rowsToOnboard.length; i += BATCH_SIZE) {
      const batch = rowsToOnboard.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map(async (row) => {
        await onboardArtwork(row, row.key);
      }));
    }

    // Refresh inventory data after save
    if (typeof fetchKpiData === 'function') {
      await fetchKpiData();
    }
    setLoading(false);
  };

  const handleTemplateUpload = (file: File) => {
    setShowStatusColumn(false); // Show the status column
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      if (!data) return;

      if (file.name.endsWith(".csv")) {
        const parsed = Papa.parse(data as string, {
          header: true,
          skipEmptyLines: true,
        });
  setTemplateData(parsed.data.map((row: any, index: number) => ({ key: index, ...row, selectedImage: null })));
      } else if (file.name.endsWith(".xlsx")) {
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const parsed = XLSX.utils.sheet_to_json<any>(sheet);
        setTemplateData(parsed.map((row, index) => ({ key: index, ...row, selectedImage: null })));
      }
      
      setIsTemplateUploaded(true);
    };
    reader.readAsBinaryString(file);
    return false;
  };

  const handleImageSelect = (value: string | undefined, key: string) => {
    if (!value) {
      // Deselect: clear the image fields for this row
      setTemplateData((prevData) =>
        prevData.map((row) =>
          row.key === key
            ? { ...row, selectedImage: undefined, selectedImageFilename: undefined }
            : row
        )
      );
      return;
    }
    // Find the selected image object by matching the URL (value) from the list
    const selectedImage = previewImages.find((img) => img.url === value);
    if (selectedImage) {
      setTemplateData((prevData) =>
        prevData.map((row) =>
          row.key === key
            ? {
                ...row,
                selectedImageFilename: selectedImage.name,
                selectedImage: selectedImage.url,
              }
            : row
        )
      );
    }
  };

  const handleArtistSelect = (value: string, recordKey: number) => {
    setTemplateData((prevData) =>
      prevData.map((row) => (row.key === recordKey ? { ...row, selectedArtist: value } : row))
    );
  };

  const handleSelectRow = (checked: boolean, recordKey: string) => {
    setSelectedRows((prevSelected) =>
      checked
        ? [...prevSelected, recordKey] // Add to selection
        : prevSelected.filter((key) => key !== recordKey) // Remove from selection
    );
  };

  const handleSelectAll = (e: any) => {
    const checked = e.target.checked;
    // Check if templateData is empty
    if (templateData.length === 0) {
      setSelectedRows([]); // No rows to select
      setIsAllSelected(false); // Ensure 'Select All' checkbox is unchecked
      setIsIndeterminate(false); // Reset indeterminate state
      return;
    }

    if (checked) {
      setSelectedRows(templateData.map((row) => row.key.toString())); // Select all rows
    } else {
      setSelectedRows([]); // Deselect all rows
    }

    setIsAllSelected(checked);
    setIsIndeterminate(false); // Reset indeterminate state
  };

  useEffect(() => {
    if (templateData.length && selectedRows.length === templateData.length) {
      setIsAllSelected(true);
      setIsIndeterminate(false); // All selected
    } else if (selectedRows.length === 0) {
      setIsAllSelected(false);
      setIsIndeterminate(false); // None selected
    } else {
      setIsIndeterminate(true); // Some selected
    }
  }, [selectedRows, templateData.length]);

  // Parses user input (e.g., "1,2,3-10") into an array of selected row keys
  const handleSelectionInput = (value: string) => {
    setSelectionInput(value);
  
    const selectedKeys = new Set<string>();
  
    value.split(",").forEach((part) => {
      if (part.includes("-")) {
        // Handle ranges like "3-7"
        const [start, end] = part.split("-").map(Number);
        if (!isNaN(start) && !isNaN(end) && start <= end) {
          for (let i = start; i <= end; i++) {
            selectedKeys.add((i-1).toString()); // Convert numbers to strings
          }
        }
      } else {
        // Handle individual values like "2"
        const num = Number(part.trim());
        if (!isNaN(num)) {
          selectedKeys.add((num-1).toString());
        }
      }
    });
  
    setSelectedRows(Array.from(selectedKeys)); // No more type errors
  }

  const columns = [
    ...(!showStatusColumn ? [
      {
        title: (
          <Checkbox
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            onChange={handleSelectAll}
          />
        ),
        key: "selectAll",
        render: (_: any, record: any) => (
          <Checkbox
            checked={selectedRows.includes(record.key.toString())} // Ensure consistency
            onChange={(e) => handleSelectRow(e.target.checked, record.key.toString())}
          />
        ),
      },
    ] : [
      {
        title: "Status",
        key: "status",
        render: (_: any, record: any) => {
          // Only show status for selected rows
          if (!selectedRows.includes(String(record.key))) return null;
          const status = rowStatuses[record.key];
          if (status === "success") return <Tag color="green">✅ Success</Tag>;
          if (status === "error") {
            const msg = rowMessages[record.key] || "Error";
            return <Tooltip title={msg}><Tag color="red">❌ Error</Tag></Tooltip>;
          }
          if (status === "pending" || status === undefined) return <><Tag color="blue">⏳ Uploading</Tag><Spin size="small" /></>;
          return null;
        },
      },
    ]),
    ...(data?.isRoot ? [{
      title: "Rep",
      dataIndex: "selectedRep",
      key: "rep",
      render: (_: any, record: any) => (
        <Select
          style={{ width: 200 }}
          onChange={(value) => handleRepSelect(value, record.key)}
          value={record.selectedRep || undefined}
          allowClear
          placeholder={<div><UserOutlined /> <span>Select a rep</span></div>}
          disabled={rowStatuses[record.key] === "success"}
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            !!option?.title && option.title.toLowerCase().includes(input.toLowerCase())
          }
        >
          {(data?.reps || [])
            .slice()
            .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
            .map((rep: any) => (
              <Option key={rep.id} value={rep.id} title={`${rep.display_name} - ${rep.username}`}>{rep.display_name}</Option>
            ))}
        </Select>
      ),
    }] : []),
    {
      title: "Artist",
      dataIndex: "selectedArtist",  // Used for render, this doesn't need to exist in initial data
      key: "artist",
      render: (_: any, record: any) => (
        <Select
          style={{ width: 200 }}
          onChange={(value) => handleArtistSelect(value, record.key)}
          value={record.selectedArtist || undefined}
          allowClear
          placeholder={<div><UserOutlined /> <span>Select an artist</span></div>}
          disabled={rowStatuses[record.key] === "success"}
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            !!option?.title && option.title.toLowerCase().includes(input.toLowerCase())
          }
        >
          {artists
            .slice()
            .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
            .map((artist: any) => (
              <Option
                key={artist.id}
                value={artist.id}
                title={`${artist.display_name} - ${artist.username}`}
              >
                {artist.display_name}
              </Option>
            ))}
        </Select>
      ),
    },
    {
      title: "Artwork",
      key: "image",
      height: "50px",
      render: (_: any, record: any) => {
        // Collect all selected images except for this row
        const selectedImages = templateData
          .filter((row) => row.key !== record.key && row.selectedImage)
          .map((row) => row.selectedImage);
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Select
              style={{ width: "300px", height: "60px" }}
              onChange={(value) => handleImageSelect(value, record.key)}
              value={record.selectedImage || undefined}
              allowClear
              placeholder={<div><PictureOutlined /> <span>Select an artwork</span></div>}
              disabled={rowStatuses[record.key] === "success"}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                !!option?.title && option.title.toLowerCase().includes(input.toLowerCase())
              }
            >
              {previewImages
                ?.slice()
                ?.sort((a, b) => a.name.localeCompare(b.name))
                ?.filter((img) =>
                  !selectedImages.includes(img.url) || record.selectedImage === img.url
                )
                ?.map((img) => (
                  <Option key={img.name} value={img.url} title={img.name}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        height: "50px",
                        overflow: "hidden",
                        maxWidth: "300px"
                      }}
                    >
                      <Image src={img.url} height={50} preview={false} />
                      <span style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "200px" }}>
                        {img.name}
                      </span>
                    </div>
                  </Option>
                ))}
            </Select>
          </div>
        );
      },
    },
    { title: "Title", dataIndex: "Title", key: "title" },
    ...(!showStatusColumn
      ? [{ title: "Description", dataIndex: "Description", key: "description" },
    { title: "Length", dataIndex: "Length", key: "length" },
    { title: "Width", dataIndex: "Width", key: "width" },
    { title: "Depth", dataIndex: "Depth", key: "depth" },
    { title: "Units", dataIndex: "Units", key: "units" },
    { title: "Year", dataIndex: "Year", key: "year" },
    { 
      title: "List Price", 
      dataIndex: "List Price", 
      key: "listPrice",
  render: (val: any) => formatNumberToReadable(Number(val)),
    },
    { title: "Original/Print", 
      dataIndex: "Original/Print", 
      key: "originalPrint",
  render: (val: any) => val === 'O' ? <Tag color="#8884d8" >Original</Tag> : <Tag color="#ffa940" >Print</Tag>,
    },
    { 
      title: "Edition Size", 
      dataIndex: "Edition Size", 
      key: "editionSize",
  render: (val: any) => val ? val : 1,
    },
    { title: "Commission", 
      dataIndex: "Commission", 
      key: "commission",
  render: (val: any) => val ? `${val}%` : '',
    },
    { title: "For Sale", 
      dataIndex: "For Sale", 
      key: "forSale",
  render: (val: any) => val?.toString().toLowerCase() === 'nfs' ? <Tag color="#f5a3a3" >Not For Sale</Tag> : <Tag color="#27ae60" >For Sale</Tag>,
     },
    { title: "Public Catalog", 
      dataIndex: "Public Catalog", 
      key: "publicCatalog",
  render: (val: any) => val?.toString().toLowerCase() === 'public' ? <Tag color="#40a9ff" >Public</Tag> : <Tag color="#f5222d" >Private</Tag>,
     }] : []),
  ];

  const fetchKpiData = async () => {
    try {
      if (!user?.rep_id) {
        setLoading(false);
        return;
      }
      // For root users, always fetch all artists and inventory
      let url = '/api-inventory-management-data';
      
      // If root and a rep is selected in the main filter, pass it
      let params: any = {
          rootUser: isRootUser(),
          userId: user.rep_id,
      };

      if (isRootUser() && selectedTargetRepId) {
          url += `?repId=${selectedTargetRepId}`;
      }

      const response = await makeRequest({
        origin: window.location.origin,
        method: 'get',
        url,
        headers: {},
        data: params,
      });
      setData(response);
      setLoading(false);
    } catch (error) {
    }
  };

  // Re-fetch when selectedTargetRepId changes (for Root filtering)
  useEffect(() => {
    if (isRootUser()) {
        fetchKpiData();
    }
  }, [selectedTargetRepId]);

  const mapArtworkFields = (row: any) => ({
    title: row.Title, // Map "Title" to "title"
    description: row.Description,
    length: row.Length,
    width: row.Width,
    depth: row.Depth,
    units: row.Units,
    year: row.Year,
    price: row["List Price"],
    finalPrice: row["List Price"], // For compatibility, but backend should use final_price
    imageUrl: row.imageUrl,
    createdByUserId: user.id,
    updatedByUserId: user.id,
    repId: data?.isRoot ? row.selectedRep : user.rep_id,
    artistUsername: artists.find((a: any) => a.id === row.selectedArtist)?.username || row.selectedArtist,
    isSaleEnabled: row["For Sale"].toLowerCase() === 'for sale' ? 1 : 0,
    copyEnabled: row["Original/Print"].toLowerCase() === 'o' ? 0 : 1,
    totalAmount: row["Edition Size"] ? row["Edition Size"] : 1,
    amountAvailable: row["Edition Size"] ? row["Edition Size"] : 1,
    commission: row.Commission,
    status: 'active',
    artistPrivate: row["Public Catalog"].toLowerCase() === 'public' ? 0 : 1,
  });

  const onboardArtwork = async (artwork: any, rowKey: any) => {
    try {
      setLoading(true);
      const transformedData = mapArtworkFields(artwork); // Map each row

      setRowStatuses(prev => ({ ...prev, [rowKey]: 'pending' })); // Set to pending

      const response = await makeRequest({
        origin: window.location.origin,
        method: 'post',
        url: '/artwork',
        headers: {},
        data: {
          rootUser: isRootUser(),
          userId: user.rep_id, // Use businessID only
          data: transformedData,
        },
        timeout: 60000,
      });
      
      setLoading(false);
      setRowStatuses(prev => ({ ...prev, [rowKey]: 'success' }));
    } catch (error: any) {
      setRowStatuses(prev => ({ ...prev, [rowKey]: 'error' }));
      setRowMessages(prev => ({ ...prev, [rowKey]: error.message || "Onboarding failed" }));
    }
  };

  useEffect(() => {
    fetchKpiData();
  }, [user]);

  const isRootUser = () => {
    return user && (user.contract_id === 0 || user.contract_id === '0');
  }

  const formatNumberToReadable = (num: any) => {
    if (num === null || num === undefined || isNaN(num)) {
      return "N/A"; // Handle invalid inputs gracefully
    }

    const number = Number(num);
    const absNumber = Math.abs(number);

    if (absNumber >= 1e9) {
      // Billions
      return `$${(number / 1e9).toFixed(2).replace(/\.00$/, "")}B`;
    } else if (absNumber >= 1e6) {
      // Millions
      return `$${(number / 1e6).toFixed(2).replace(/\.00$/, "")}M`;
    } else if (absNumber >= 1e3) {
      return `$${(number / 1e3).toFixed(2).replace(/\.00$/, "")}K`; // Thousands
    } else {
      // Less than a thousand
      return `$${number.toFixed(2).replace(/\.00$/, "")}`; // Less than a thousand
    }
  }

  const artists = useMemo(() => {
    // Build artists from inventory data, using artist_display_name and username
    const inventory = Array.isArray(data?.inventory) ? data.inventory : (Array.isArray(data?.inventory?.rows) ? data.inventory.rows : []);
    const artistMap: Record<string, { id: string; display_name: string; username: string }> = {};
    inventory.forEach((item: any) => {
      const id = item.created_by_artist_id;
      if (!artistMap[id]) {
        artistMap[id] = {
          id,
          display_name: item.artist_display_name || '',
          username: item.username || '',
        };
      }
    });
    return Object.values(artistMap);
  }, [data?.artists, data?.isRoot]);

  const inventoryData: Artwork[] = useMemo(() => {
    // For root users, backend sends inventory as array, for non-root may send .rows
    let inventory = Array.isArray(data?.inventory) ? data.inventory : (Array.isArray(data?.inventory?.rows) ? data.inventory.rows : []);
    if (!user || !inventory.length) return [];
    // Map backend fields to Artwork interface
    const mapRowToArtwork = (row: any): Artwork => ({
      artist_private: row.artist_private,
      id: row.id,
      title: row.title,
      description: row.description,
      length: row.length,
      width: row.width,
      depth: row.depth,
      units: row.units,
      year: row.year,
      is_owned_by_collector: row.is_owned_by_collector,
      royalty: row.royalty,
      price: row.final_price,
      final_price: row.final_price,
      image_url: row.image_url,
      created_date: row.created_date,
      created_by_user_id: row.created_by_user_id,
      updated_date: row.updated_date,
      updated_by_user_id: row.updated_by_user_id,
      artwork_history: row.artwork_history || [],
      owner_identity_claims: row.owner_identity_claims || [],
      materials: row.materials || [],
      formats: row.formats || [],
      genres: row.genres || [],
      created_by_artist_id: row.created_by_artist_id,
      waiting_for_artist: row.waiting_for_artist,
      status: row.status,
      current_owner: row.current_owner,
      is_sale_enabled: row.is_sale_enabled,
      cert_ref_url: row.cert_ref_url,
      copy_enabled: row.copy_enabled,
      amount_available: row.amount_available,
      copy_reference_id: row.copy_reference_id,
      total_amount: row.total_amount,
      orders_print_sold: row.orders_print_sold,
      parent_total_amount: row.parent_total_amount,
      sold_position: row.sold_position,
      placard_id: row.placard_id,
      contract_data: row.contract_data || { hasRepRegistry: false, contractId: null, status: '', fee: 0 },
      commission: row.commission,
      active_rep_ids: row.active_rep_ids,
    });

    let processedInventory: Artwork[];
    if (user.contract_id === 0 || user.contract_id === '0') {
      processedInventory = inventory.map(mapRowToArtwork);
    } else {
      processedInventory = inventory.filter((row: any) => row.has_contract !== false).map(mapRowToArtwork);
    }

    if (!searchQuery) {
      return processedInventory;
    }

    const lowercasedQuery = searchQuery.toLowerCase();
    // For root users, artists may be in data.artists.rows
    const flatArtists = Array.isArray(data?.artists?.rows) ? data.artists.rows : artists;
    return processedInventory.filter(item => {
      const artist = flatArtists.find((a: any) => a.id === item.created_by_artist_id);
      const artistName = artist ? artist.display_name : '';

      return (item.id && item.id.toLowerCase().includes(lowercasedQuery)) ||
        (item.title && item.title.toLowerCase().includes(lowercasedQuery)) ||
        (artistName && artistName.toLowerCase().includes(lowercasedQuery)) ||
        (item.current_owner && item.current_owner.toLowerCase().includes(lowercasedQuery))
    });
  }, [data.inventory, user, searchQuery]);


  return (
    <div>
      {/* Moved Modal component */}
      <Modal
        title="Artwork Details"
        open={isModalVisible}
        onOk={() => setIsModalVisible(false)}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            Close
          </Button>,
          <Button key="save" type="primary" onClick={async () => {
            if (!editArtwork?.id) return;

            try {
              // Update Artwork metadata (always allowed for root and non-root users)
              await makeRequest({
                origin: window.location.origin,
                method: 'put',
                url: `/artwork/${editArtwork.id}`,
                data: {
                  ...editArtwork,
                  updated_by_user_id: user?.rep_id,
                },
              });

              // Handle Commission Update with History
              // For Root: Use modalTargetRepId (only if selected)
              // For Non-Root: Default logic (updates their own)

              // Only update commission if it changed or if we are Root forcing an assignment
              const commissionChanged = selectedArtwork && editArtwork.commission !== undefined && Number(editArtwork.commission) !== Number(selectedArtwork.commission);

              // For Root, we only update if they selected a rep AND (the commission changed OR they want to ensure contract exists)
              // For Non-Root, we update if commission changed
              const shouldUpdateCommission = (isRootUser() && modalTargetRepId) || (!isRootUser() && commissionChanged);

              if (shouldUpdateCommission && editArtwork.commission !== undefined && editArtwork.commission !== null) {
                 await makeRequest({
                    origin: window.location.origin,
                    method: 'post',
                    url: '/api/artworks/update-commissions',
                    data: {
                        targetRepId: isRootUser() ? modalTargetRepId : undefined,
                        updates: [{
                            artworkId: editArtwork.id,
                            commission: Number(editArtwork.commission)
                        }]
                    }
                 });
              }

              setSelectedArtwork(editArtwork);
              setIsModalVisible(false);
              // Refresh inventory after update
              await fetchKpiData();
            } catch (error) {
              // Optionally show error to user
              console.error('Failed to update artwork:', error);
            }
          }}>
            Save
          </Button>,
        ]}
      >
        {editArtwork && (
          <div>
            <div style={{ position: 'relative' }}>
              <Image width={'100%'} src={editArtwork.image_url ?? ''} />
              <Button
                icon={<CameraOutlined />}
                type="primary"
                style={{ marginTop: 8 }}
                block
                onClick={() => setIsImageEditorVisible(true)}
              >
                Edit Image (Crop, Rotate, Replace)
              </Button>
            </div>
            <Divider/>
            {/* Root User Rep Selector in Modal */}
            {isRootUser() && (
                <div style={{ marginBottom: 16 }}>
                    <Text strong>Select Representative: </Text>
                    <Select
                        placeholder="Select Rep"
                        style={{ width: '100%' }}
                        value={modalTargetRepId}
                        onChange={async (val) => {
                            setModalTargetRepId(val);
                            // Fetch active contract for this rep + artwork
                            if (val && editArtwork.id) {
                                try {
                                    const res = await makeRequest({
                                        origin: window.location.origin,
                                        method: 'get',
                                        url: `/api/contracts/active?artworkId=${editArtwork.id}&repId=${val}`,
                                    });
                                    // If contract exists (source != 'none'), update commission field
                                    // If 'none', we might want to default to 0 or keep current?
                                    // "show the rep that already have a contract allocated... or if no contract... create one"
                                    if (res && res.commission !== undefined) {
                                        setEditArtwork(prev => prev ? ({ ...prev, commission: res.commission }) : null);
                                    } else {
                                        // Default to 0 or blank if no contract found
                                        setEditArtwork(prev => prev ? ({ ...prev, commission: 0 }) : null);
                                    }
                                } catch (e) {
                                    console.error('Error fetching contract:', e);
                                }
                            }
                        }}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            !!option?.title && option.title.toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {reps.map((rep: any) => {
                             const isActive = editArtwork?.active_rep_ids?.includes(rep.id);
                             return (
                                <Option key={rep.id} value={rep.id} title={`${rep.display_name} - ${rep.username}`}>
                                    <span style={{ fontWeight: isActive ? 'bold' : 'normal' }}>
                                        {rep.display_name} {isActive ? '(Active)' : ''}
                                    </span>
                                </Option>
                            );
                        })}
                    </Select>
                    <Divider />
                </div>
            )}

            {/* Last Updated Fields */}
            <Row gutter={16} style={{ marginBottom: 8 }}>
              <Col span={12}>
                <Text strong>Last Updated Date: </Text>
                <Input
                  value={editArtwork.updated_date ? new Date(editArtwork.updated_date).toLocaleString() : 'N/A'}
                  disabled
                />
              </Col>
              <Col span={12}>
                <Text strong>Last Updated By: </Text>
                <Input
                  value={editArtwork.updated_by_user_id || 'N/A'}
                  disabled
                />
              </Col>
            </Row>
            <Text strong>Title: </Text>
              <Input
                value={editArtwork.title}
                onChange={e => setEditArtwork({ ...editArtwork, title: e.target.value })}
                placeholder="Title"
                style={{ marginBottom: 8 }}
                disabled={editArtwork.copy_enabled && Number(editArtwork.amount_available) < Number(editArtwork.total_amount)}
              />
            <Text strong>Artist: </Text>
            <Input
              value={(() => {
                const artist = artists.find((a: any) => a.id === editArtwork.created_by_artist_id);
                if (artist) {
                  return artist.display_name || artist.username || editArtwork.created_by_artist_id;
                }
                return editArtwork.created_by_artist_id;
              })()}
              disabled
              style={{ marginBottom: 8 }}
            /><br/>
            <Text strong>Artwork ID: </Text>
            <Input
              value={editArtwork.id}
              disabled
              style={{ marginBottom: 8 }}
            /><br/>
            <Text strong>Sale Price: </Text>
            <Input
              value={editArtwork.final_price}
              onChange={e => setEditArtwork({ ...editArtwork, final_price: Number(e.target.value) || undefined })}
              placeholder="Sale Price"
              style={{ marginBottom: 8 }}
            /><br/>
            <Text strong>Commission (%): </Text>
            <Input
              value={editArtwork.commission}
              onChange={e => {
                  const val = Number(e.target.value);
                  if (val >= 0) {
                      setEditArtwork({ ...editArtwork, commission: val });
                  }
              }}
              placeholder="Commission"
              type="number"
              min={0}
              style={{ marginBottom: 8 }}
            /><br/>
            <Text strong>Artwork Type: </Text>
            <Select
              value={editArtwork.copy_enabled ? 'Print' : 'Original'}
              style={{ width: 120, marginBottom: 8 }}
              disabled={true}
            >
              <Option value="Original">Original</Option>
              <Option value="Print">Print</Option>
            </Select><br/>
            {editArtwork.copy_enabled &&
              <>
                <Text strong>Total Amount: </Text>
                <Input
                  value={editArtwork.total_amount}
                  onChange={e => setEditArtwork({ ...editArtwork, total_amount: Number(e.target.value) || null })}
                  placeholder="Total Amount"
                  style={{ marginBottom: 8 }}
                  disabled={!isRootUser() && Number(editArtwork.amount_available) < Number(editArtwork.total_amount)}
                /><br/>
                <Text strong>Amount Available: </Text>
                <Input
                  value={editArtwork.amount_available}
                  onChange={e => setEditArtwork({ ...editArtwork, amount_available: Number(e.target.value) || null })}
                  placeholder="Amount Available"
                  style={{ marginBottom: 8 }}
                  disabled={!isRootUser() && Number(editArtwork.amount_available) < Number(editArtwork.total_amount)}
                /><br/>
              </>
            }
            <Text strong>Year: </Text>
            <Input
              value={editArtwork.year}
              onChange={e => setEditArtwork({ ...editArtwork, year: Number(e.target.value) || null })}
              placeholder="Year"
              style={{ marginBottom: 8 }}
              disabled={editArtwork.copy_enabled && Number(editArtwork.amount_available) < Number(editArtwork.total_amount)}
            /><br/>
            <Text strong>Description: </Text>
            <Input.TextArea
              value={editArtwork.description}
              onChange={e => setEditArtwork({ ...editArtwork, description: e.target.value })}
              placeholder="Description"
              style={{ marginBottom: 8 }}
              autoSize={{ minRows: 2, maxRows: 6 }}
              disabled={editArtwork.copy_enabled && Number(editArtwork.amount_available) < Number(editArtwork.total_amount)}
            /><br/>
            <Row gutter={16}>
              <Col span={6}>
                <Text strong>Lengh: </Text>
                <Input
                  value={editArtwork.length}
                  onChange={e => setEditArtwork({ ...editArtwork, length: Number(e.target.value) || null })}
                  placeholder="Lengh"
                  style={{ marginBottom: 8 }}
                  disabled={editArtwork.copy_enabled && Number(editArtwork.amount_available) < Number(editArtwork.total_amount)}
                />
              </Col>
              <Col span={6}>
                <Text strong>Width: </Text>
                <Input
                  value={editArtwork.width}
                  onChange={e => setEditArtwork({ ...editArtwork, width: Number(e.target.value) || null })}
                  placeholder="Width"
                  style={{ marginBottom: 8 }}
                  disabled={editArtwork.copy_enabled && Number(editArtwork.amount_available) < Number(editArtwork.total_amount)}
                />
              </Col>
              <Col span={6}>
                <Text strong>Hight: </Text>
                <Input
                  value={editArtwork.depth}
                  onChange={e => setEditArtwork({ ...editArtwork, depth: Number(e.target.value) || null })}
                  placeholder="Hight"
                  style={{ marginBottom: 8 }}
                  disabled={editArtwork.copy_enabled && Number(editArtwork.amount_available) < Number(editArtwork.total_amount)}
                />
              </Col>
              <Col span={6}>
                <Text strong>Units: </Text>
                <Input
                  value={editArtwork.units}
                  onChange={e => setEditArtwork({ ...editArtwork, units: e.target.value })}
                  placeholder="Units"
                  style={{ marginBottom: 8 }}
                  disabled={editArtwork.copy_enabled && Number(editArtwork.amount_available) < Number(editArtwork.total_amount)}
                />
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Image Editor Modal */}
      <Modal
        title="Edit Artwork Image"
        open={isImageEditorVisible}
        onCancel={() => setIsImageEditorVisible(false)}
        footer={null}
        width={800}
        destroyOnHidden
      >
        <Spin spinning={uploadingImage} tip="Uploading image...">
          {editArtwork?.image_url && (
            <ImageCropper
              initialImageUrl={editArtwork.image_url}
              onImageCropped={handleImageEdited}
              onCancel={() => setIsImageEditorVisible(false)}
            />
          )}
        </Spin>
      </Modal>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <Title level={5} style={{ marginBottom: 0 }}>
            Welcome {user.display_name}!{' '}
          </Title>
          {artists.length > 0 && (
            <Text type="secondary" style={{ fontSize: '14px', fontWeight: 'lighter' }}>
              You are representing {artists.length} artists
            </Text>
          )}
        </div>
        <div>
          {showInventoryContent ? (
            <Button type="default" onClick={() => setShowInventoryContent(false)} style={{ minWidth: 180 }}>
              Hide Import Inventory
            </Button>
          ) : (
            <Button type="primary" onClick={() => setShowInventoryContent(true)} style={{ minWidth: 180 }}>
              Import Inventory
            </Button>
          )}
        </div>
      </div>
      {!showInventoryContent ? (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span style={{ fontWeight: 500, fontSize: 18 }}>Current Inventory</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {isRootUser() && (
                    <Select
                        placeholder="Select Rep to Update"
                        style={{ width: 200 }}
                        value={selectedTargetRepId}
                        onChange={(val) => setSelectedTargetRepId(val)}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            !!option?.title && option.title.toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {reps.map((rep: any) => (
                            <Option key={rep.id} value={rep.id} title={`${rep.display_name} - ${rep.username}`}>
                                {rep.display_name}
                            </Option>
                        ))}
                    </Select>
                )}
                <Input
                  placeholder="Comm. %"
                  value={massCommission}
                  onChange={(e) => {
                      const val = Number(e.target.value);
                      if (val >= 0) {
                          setMassCommission(e.target.value);
                      }
                  }}
                  style={{ width: 100 }}
                  type="number"
                  min={0}
                />
                <Button
                  onClick={async () => {
                    if (!massCommission || isNaN(Number(massCommission)) || selectedInventoryRows.length === 0) return;
                    if (isRootUser() && !selectedTargetRepId) {
                        alert('Please select a Representative first.');
                        return;
                    }
                    setLoading(true);
                    try {
                      await makeRequest({
                        origin: window.location.origin,
                        method: 'post',
                        url: '/api/artworks/update-commissions',
                        data: {
                          targetRepId: isRootUser() ? selectedTargetRepId : undefined,
                          updates: selectedInventoryRows.map(id => ({
                            artworkId: id,
                            commission: Number(massCommission)
                          }))
                        }
                      });
                      setSelectedInventoryRows([]);
                      setMassCommission('');
                      await fetchKpiData();
                    } catch (e) {
                      console.error(e);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={selectedInventoryRows.length === 0 || !massCommission || (isRootUser() && !selectedTargetRepId)}
                >
                  Update Selected
                </Button>
                <Input
                  placeholder="Search by Artwork ID, Title, or Artist"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{ width: 280 }}
                  allowClear
                />
              </div>
            </div>
          }
          style={{ marginBottom: '2rem' }}
        >
          <Table
            rowSelection={{
              selectedRowKeys: selectedInventoryRows,
              onChange: (keys) => setSelectedInventoryRows(keys),
            }}
            dataSource={inventoryData}
            rowKey="id"
            columns={[ 
              {
                title: 'Action',
                key: 'action',
                render: (_: any, record: Artwork) => {
                  return (
                    <span>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={async () => {
                          if (!canEditArtwork(record)) return;
                          setSelectedArtwork(record);
                          setEditArtwork({ ...record });
                          
                          // Pre-select Rep for Root users
                          if (isRootUser()) {
                              // Use selected filter OR the first active rep (latest updated)
                              const repToSelect = selectedTargetRepId || (record.active_rep_ids && record.active_rep_ids.length > 0 ? record.active_rep_ids[0] : undefined);
                              setModalTargetRepId(repToSelect);

                              if (repToSelect) {
                                  try {
                                      // Fetch active contract to pre-fill commission
                                      const res = await makeRequest({
                                          origin: window.location.origin,
                                          method: 'get',
                                          url: `/api/contracts/active?artworkId=${record.id}&repId=${repToSelect}`,
                                      });
                                      if (res && res.commission !== undefined) {
                                          setEditArtwork(prev => prev ? ({ ...prev, commission: res.commission }) : null);
                                      } else {
                                          setEditArtwork(prev => prev ? ({ ...prev, commission: 0 }) : null);
                                      }
                                  } catch (e) {
                                      console.error('Error pre-fetching contract:', e);
                                  }
                              }
                          } else {
                              // Reset modal rep for non-root (or if no logic needed)
                              setModalTargetRepId(undefined);
                          }
                          
                          setIsModalVisible(true);
                        }}
                        aria-label="Edit"
                        disabled={!canEditArtwork(record)}
                        style={{ cursor: !canEditArtwork(record) ? 'not-allowed' : 'pointer' }}
                      />
                    </span>
                  );
                },
              },
              {
                title: 'ArtworkID',
                dataIndex: 'id',
                key: 'id',
                render: (artworkId: string) => (
                  <a href={`${getMarketplaceUrl()}${artworkId}`} target="_blank" rel="noopener noreferrer">
                    {artworkId}
                  </a>
                ),
              },
              {
                title: "Image",
                dataIndex: "image_url",
                key: "image",
                render: (url: string) => (
                  <Image src={url || defaultImageUrl} width={50} preview />
                ),
              },
              { title: "Title", dataIndex: "title", key: "title" },
              {
                title: "Artist",
                dataIndex: "created_by_artist_id",
                key: "artist",
                render: (artistId: string) => {
                  const artist = artists.find((a: any) => a.id === artistId);
                  return artist ? (artist.display_name || artist.username || artistId) : artistId;
                },
              },
              {
                title: "Dimensions",
                key: "dimensions",
                render: (record: Artwork) => (
                  <span>{record.length} x {record.width} x {record.depth} {record.units}</span>
                )
              },
              {
                title: "List Price",
                dataIndex: "price",
                key: "listPrice",
                render: (val: any) => formatNumberToReadable(Number(val)),
              },
              {
                title: "Commission",
                key: "commission",
                render: (_: any, record: Artwork) => {
                  return record.commission ? `${record.commission}%` : '0%';
                },
              },
              {
                title: "Type",
                dataIndex: "copy_enabled",
                key: "type",
                render: (val: any) => (val ? <Tag color="#ffa940">Print</Tag> : <Tag color="#8884d8">Original</Tag>),
              },
              {
                title: "Qty Available",
                key: "qtyAvailable",
                render: (record: Artwork) =>
                  record.copy_enabled
                    ? `${record.amount_available}/${record.total_amount}`
                    : "1/1",
              },
              {
                title: "For Sale",
                dataIndex: "is_sale_enabled",
                key: "forSale",
                render: (val: any) =>
                  val ? (
                    <Tag color="#27ae60">For Sale</Tag>
                  ) : (
                    <Tag color="#f5a3a3">Not For Sale</Tag>
                  ),
              },
              {
                title: "Visibility",
                dataIndex: "artist_private",
                key: "publicCatalog",
                render: (val: any) =>
                  val ? (
                    <Tag color="#f5222d">Private</Tag>
                  ) : (
                    <Tag color="#40a9ff">Public</Tag>
                  ),
              },
            ]}
          />
        </Card>
      ) : null}
      {showInventoryContent && (
        artists.length > 0 ? (
          <>
            {/* The close button is now handled by the top bar toggle */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12} lg={12} span={12}>
                                <Card title="Upload Assets">
                                  <div style={{ display: "flex", gap: "10px"}}>
                                    <Upload
                                      beforeUpload={handleTemplateUpload}
                                      showUploadList={false}
                                    >
                                      <Button style={{ width: "200px" }} icon={<UploadOutlined />}>Select Template</Button>
                                    </Upload>
                
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        color: "#333",
                                      }}
                                    >
                                      {isTemplateUploaded ? (
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                          <span>{templateData.length} rows found in template</span>
                                        </div>
                                      ) : (
                                        "Status: Not Uploaded"
                                      )}
                                    </div>
                                  </div>
                
                                  <div style={{ display: "flex", gap: "10px", marginTop: "0.5rem"}}>
                                    <Upload
                                      directory
                                      showUploadList={false}
                                      beforeUpload={() => false}
                                      onChange={handleFolderSelect}
                                    >
                                      <Button style={{ width: "200px" }} icon={<UploadOutlined />}>Select Image Folder</Button>
                                    </Upload>
                
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        color: "#333",
                                      }}
                                    >
                                      {isImageUploaded ? (
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                          <span>{imageList.length} images found</span>
                                        </div>
                                      ) : (
                                        "Status: Not Uploaded"
                                      )}
                                    </div>
                                  </div>
                                </Card>        </Col>

        <Col xs={24} sm={24} md={12} lg={12} span={12}>
          <Card title="Batch Update">
            <div style={{ display: "flex", gap: "10px" }}>
              <Select
                placeholder={<div><UserOutlined /> <span>Select an artist</span></div>}
                style={{ width: "200px" }}
                onChange={(value) => setNewArtist(value)}
                value={newArtist || undefined}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  !!option?.title && option.title.toLowerCase().includes(input.toLowerCase())
                }
              >
                {artists
                  .slice()
                  .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
                  .map((artist: any) => (
                    <Option key={artist.id} value={artist.id} title={`${artist.display_name}`}>
                      {artist.display_name}
                    </Option>
                  ))}
              </Select>

              <Button type="default" onClick={handleUpdateSelectedArtists} disabled={!newArtist || selectedRows.length === 0}>
                Update Selected Rows
              </Button>
            </div>
          </Card>
        </Col>
      </Row> 

      <Divider />
      
      
      <div style={{ display: "flex", gap: "10px", marginTop: "0.5rem", alignItems: "center" }}>
        <Button
          type="default"
          disabled={
            !isSaveEnabled || allUploadsDone ||
            (selectedArtwork && !canEditArtwork(selectedArtwork))
          }
          onClick={handleSave}
        >
          Save Selected
        </Button>
        <a
          href="/inventory_template_example.csv"
          download
          style={{ display: 'inline-block' }}
        >
          <Button type="default">Download Template</Button>
        </a>
        <Input
          placeholder="Enter range (e.g. 1-5,7,9-12)"
          value={selectionInput}
          onChange={(e) => handleSelectionInput(e.target.value)}
          style={{ width: 200, marginLeft: 8 }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "#333",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            { allUploadsDone  ? 
            <span>
              {successCount === 1 ? "1 artwork onboarded successfully!" : 
                `${successCount} artworks onboarded successfully!`}
            </span>
            : null
            }
          </div>
        </div>
      </div>
      
      <div style={{ overflowY: "auto", marginTop: "1rem"}}>
        <Table 
          columns={columns} 
          dataSource={templateData} 
          rowKey="key" 
          scroll={{ x: "max-content" }} 
          pagination={{ pageSize: 100 }}
        />
      </div>
          </>
        ) : (
          <Skeleton active />
        )
      )}
    </div>
  );
};

export default InventoryManagement;
